/* Self-contained PNG render of the actual preview. No remote rendering service.
 * Confirmed physical dimensions use 300 dpi. Otherwise the file is an explicit draft.
 */
globalThis.OikosLabelFile = {
  async withDPI(blob, dpi) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const chunk = new Uint8Array(21); const view = new DataView(chunk.buffer);
    view.setUint32(0, 9); chunk.set([112, 72, 89, 115], 4); // pHYs
    const ppm = Math.round(dpi / 0.0254); view.setUint32(8, ppm); view.setUint32(12, ppm); chunk[16] = 1;
    let crc = 0xffffffff;
    for (const byte of chunk.subarray(4, 17)) { crc ^= byte; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); }
    view.setUint32(17, (crc ^ 0xffffffff) >>> 0);
    const parts = [bytes.subarray(0, 8)];
    for (let offset = 8; offset < bytes.length;) {
      const length = new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
      const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
      if (type !== 'pHYs') parts.push(bytes.subarray(offset, offset + length + 12));
      if (type === 'IHDR') parts.push(chunk);
      offset += length + 12;
    }
    return new Blob(parts, { type: 'image/png' });
  },
  async create(editor) {
    if (editor.getMode() === 'upload') {
      const file = editor.designFile.files[0];
      if (!file || editor.designPending || editor.designFile.validationMessage) throw new Error('Velg en gyldig ferdig etikett');
      return file; // Complete customer design is already the print file; never add overlays.
    }
    if (editor.imagePending || (editor.getMode() === 'builder' && editor.file.validationMessage)) throw new Error('Vent til bildet er kontrollert');
    await document.fonts.ready;
    editor.updateTypography();
    if (editor.colorsValid === false) throw new Error('Kontroller tekstfargene');
    const label = editor.label;
    const bounds = label.getBoundingClientRect();
    if (!bounds.width || !bounds.height) throw new Error('Forhåndsvisningen er ikke synlig');
    const option = editor.size.selectedOptions[0];
    const physical = option.dataset.dimensionsConfirmed === 'true' && Number(option.dataset.width) > 0 && Number(option.dataset.height) > 0;
    const scale = physical ? Number(option.dataset.width) / 25.4 * 300 / bounds.width : 2400 / Math.max(bounds.width, bounds.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bounds.width * scale); canvas.height = Math.round(bounds.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Nettleseren støtter ikke bildeeksport');
    ctx.scale(canvas.width / bounds.width, canvas.height / bounds.height);
    ctx.fillStyle = getComputedStyle(label).backgroundColor;
    ctx.fillRect(0, 0, bounds.width, bounds.height);
    const visible = element => element && !element.hidden && element.getClientRects().length > 0;
    async function drawImage(selector) {
      const image = editor.querySelector(selector);
      if (!visible(image)) return;
      if (!image.complete) await image.decode();
      if (!image.naturalWidth) throw new Error('Etikettbildet kunne ikke åpnes');
      const box = image.getBoundingClientRect();
      const fit = getComputedStyle(image).objectFit;
      const ratio = fit === 'cover' ? Math.max(box.width / image.naturalWidth, box.height / image.naturalHeight) : Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight);
      const width = image.naturalWidth * ratio, height = image.naturalHeight * ratio;
      ctx.save(); ctx.beginPath(); ctx.rect(box.x - bounds.x, box.y - bounds.y, box.width, box.height); ctx.clip();
      ctx.drawImage(image, box.x - bounds.x + (box.width - width) / 2, box.y - bounds.y + (box.height - height) / 2, width, height);
      ctx.restore();
    }
    function drawText(selector) {
      const element = editor.querySelector(selector);
      if (!visible(element) || !element.textContent || !element.firstChild) return;
      const style = getComputedStyle(element);
      ctx.save();
      ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      ctx.fillStyle = style.color; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      if (editor.getMode() === 'oikos') { ctx.shadowColor = '#3b170e'; ctx.shadowBlur = 3; ctx.shadowOffsetY = 1; }
      const metrics = ctx.measureText('Hg');
      const ascent = metrics.fontBoundingBoxAscent || parseFloat(style.fontSize) * 0.8;
      const text = element.textContent; const node = element.firstChild; const range = document.createRange();
      const lines = []; let current = null; let offset = 0;
      for (const char of text) {
        const end = offset + char.length;
        if (char === '\n') { current = null; offset = end; continue; }
        range.setStart(node, offset); range.setEnd(node, end);
        const rect = range.getBoundingClientRect();
        if (!current || Math.abs(rect.top - current.top) > 1) { current = { text: '', left: rect.left, top: rect.top }; lines.push(current); }
        current.text += char; offset = end;
      }
      for (const line of lines) ctx.fillText(line.text, line.left - bounds.left, line.top - bounds.top + ascent);
      range.detach(); ctx.restore();
    }
    if (editor.getMode() === 'oikos') {
      await drawImage('[data-oikos-image]');
      drawText('[data-oikos-greeting-preview]');
    } else {
      await drawImage('[data-background]');
      await drawImage('[data-frame-overlay]');
      await drawImage('[data-photo]');
      drawText('[data-preview-source="message"]'); drawText('[data-preview-source="title"]');
      ctx.save(); ctx.globalAlpha = 0.6; ctx.strokeStyle = getComputedStyle(label).color; ctx.lineWidth = 1;
      ctx.strokeRect(bounds.width * 0.04, bounds.height * 0.04, bounds.width * 0.92, bounds.height * 0.92); ctx.restore();
    }
    let blob;
    try { blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png')); }
    catch { throw new Error('Bildet tillater ikke eksport. Bruk en etikettfil fra temaet eller en lokal opplasting'); }
    if (!blob) throw new Error('PNG-filen kunne ikke lages');
    if (physical) blob = await this.withDPI(blob, 300);
    if (blob.size > 10 * 1024 * 1024) throw new Error('Etikettfilen er større enn 10 MB');
    const name = `oikos-${option.dataset.pricingKey}-${physical ? 'etikett' : 'utkast'}.png`;
    return new File([blob], name, { type: 'image/png' });
  }
};
